import React, { useEffect, useCallback, useMemo, useState, FC, Dispatch, SetStateAction, useRef } from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';
import { Cell } from 'react-table';
import { getIdNumber } from '@tradingblock/api';
import { Table, useParentSizeContext, DataTableProps, ColumnWithSorting, Loading } from '@tradingblock/components';
import { SizeType, CashieringBaseEntity, CashieringSearchRequest } from '@tradingblock/types';
import { useBboxDimensions } from '../../../../blocks/shared/useBboxDimensions';
import { useDispatcher } from '../../../../data/global/hooks';
import { useStateSelector } from '../../../../data/global/dataSelectors';
import { isFetchingCashieringSearchEntities } from '../../../../data/global/selectors/admin/cashieringSelectors';
import { useCashieringActions, useCashieringData } from '../useCashiering';
import {
  AdminCashieringActions,
  AdminCashieringSearchActions,
} from '../../../../data/global/actions/admin/AdminCashieringActions';
import { TransactionCell } from './table/CashieringTransactionCell';
import { CashieringActionCell } from './table/CashieringActionCell';
import { CashieringMethodCell } from './table/CashieringMethodCell';
import { CashieringNoteCell } from './table/CashieringNoteCell';
import { Modal as BootstrapModal, Button, Modal } from 'react-bootstrap';
import { exportToCsv, formatMoney } from '../../../../blocks/Analytics/AnalyticsHelper';

const CashieringAdminReviewColumns: ColumnWithSorting<CashieringBaseEntity>[] = [
  {
    Header: 'Account',
    accessor: v => v.accountNumber,
    sortType: 'alphanumeric',
    id: 'AccountNumber',
  },
  {
    Header: 'Name',
    accessor: v => v.accountName,
    sortType: 'alphanumeric',
    id: 'AccountName',
  },
  {
    Header: 'Rep Code',
    accessor: v => v.repCode,
    sortType: 'alphanumeric',
    id: 'RepCode',
  },
  {
    Header: 'Transaction',
    id: 'transaction',
    Cell: ({ row }: Cell<CashieringBaseEntity>) => {
      const { transfer, status, relationship, transferRecipientBank, transferWireInstruction } = row.original;
      return (
        <TransactionCell {...{ transfer, status, relationship, transferRecipientBank, transferWireInstruction }} />
      );
    },
  },
  {
    Header: 'Method',
    id: 'method',
    Cell: ({ row }: Cell<CashieringBaseEntity>) => {
      const { transfer, relationship } = row.original;
      return <CashieringMethodCell {...{ transfer, relationship }} />;
    },
  },
  {
    Header: 'Date',
    accessor: v => v.date,
    sortType: 'alphanumeric',
    sortDescFirst: true,
    id: 'Date',
    Cell: ({ row }: Cell<CashieringBaseEntity>) => {
      const dateVal = dayjs(row.original.date);
      if (dateVal && dateVal.isValid()) {
        return (
          <>
            {dateVal.format('M/DD/YY')}
            <br />
            {dateVal.format('hh:mm A')}
          </>
        );
      }
      return <></>;
    },
  },
  {
    Header: 'Account Status',
    id: 'accountStatus',
    sortType: 'alphanumeric',
    Cell: ({ row }: Cell<CashieringBaseEntity>) => {
      const { accountStatus } = row.original;
      const getAccountStatus = useCallback((status: string) => {
        // format status to place a space between words
        const formattedStatus = status.replace(/([A-Z])/g, ' $1').trim();
        switch (status) {
          case 'Active':
            return (
              <span className="pos" style={{ fontWeight: 500 }}>
                {formattedStatus}
              </span>
            );
          case 'Inactive':
            return (
              <span className="neg" style={{ fontWeight: 500 }}>
                {formattedStatus}
              </span>
            );
          case 'Closed':
            return (
              <span className="neg" style={{ fontWeight: 500 }}>
                {formattedStatus}
              </span>
            );
          default:
            return <span className="warn">{formattedStatus}</span>;
        }
      }, []);

      return <>{getAccountStatus(accountStatus)}</>;
    },
  },
  {
    Header: 'Notes',
    id: 'note',
    Cell: ({ row }: Cell<CashieringBaseEntity>) => {
      const { note, adminNote, transfer, relationship, transferWireInstruction, accountId } = row.original;
      const id = transfer
        ? transfer.transferId
        : relationship
          ? relationship.relationshipId
          : transferWireInstruction
            ? transferWireInstruction.transferWireInstructionId
            : undefined;
      const type = transfer
        ? 'transfers'
        : relationship
          ? 'relationships'
          : transferWireInstruction
            ? 'wireInstructions'
            : undefined;
      if (id && type && (note || adminNote)) {
        return (
          <>
            <CashieringNoteCell accountId={accountId} id={getIdNumber(id)} type={type} />
          </>
        );
      }
      return <></>;
    },
  },
  {
    Header: 'Action',
    id: 'action',
    className: 'text-center',
    Cell: ({ row }: Cell<CashieringBaseEntity>) => {
      const {
        status,
        transfer,
        relationship,
        type,
        accountId,
        transferRecipientBank,
        transferWireInstruction,
      } = row.original;

      const handleClick = useCallback(() => {
        sessionStorage.setItem('cashieringSearch', JSON.stringify({ index: row.index }));
      }, [row]);

      return (
        <div className="text-center" onClick={() => handleClick()}>
          <CashieringActionCell
            {...{ status, transfer, relationship, type, accountId, transferRecipientBank, transferWireInstruction }}
          />
        </div>
      );
    },
  },
  {
    Header: 'Filter',
    id: 'filter',
    Cell: ({ row }: Cell<CashieringBaseEntity>) => {
      const { search: onSearch, setPage, setType, setStatus } = useCashieringActions();
      // searchValue should default to accountNumber, if no accountNumber, then use accountName
      const searchValue = row.original.accountNumber
        ? row.original.accountNumber.trim()
        : row.original.accountName.trim();
      const handleSetFilter = useCallback(() => {
        setPage(0);
        setType([]);
        setStatus([]);
        onSearch(searchValue);
        sessionStorage.removeItem('cashieringSearch');
        row.original.handleSetSelectedRow(undefined);
      }, [row.original.accountId]);

      return (
        <div className="text-center">
          <button onClick={() => handleSetFilter()}>
            <i className="fal fa-filter" />
          </button>
        </div>
      );
    },
  },
];

const CashieringTableContent: React.FunctionComponent<DataTableProps<CashieringBaseEntity>> = props => {
  const b = useParentSizeContext();
  const [, ref] = useBboxDimensions<HTMLDivElement>(250);

  return (
    <div ref={ref} className="table-fixed-wrapper">
      <Table {...props} size={SizeType.lg} windowSize={b} />
    </div>
  );
};

interface CashieringDownloadCSVModalProps {
  show: boolean,
  setShow: Dispatch<SetStateAction<boolean>>,
  columns: JSX.Element[],
  total: number,
  downloadAction: any,
  setIncludeList: Dispatch<SetStateAction<number[]>>,
  includeList: number[],
}

const CashieringDownloadCSVModal: FC<CashieringDownloadCSVModalProps> = ({
  show,
  setShow,
  columns,
  downloadAction,
  setIncludeList,
  includeList,
}) => {

  const toggleShow = useCallback((shouldShow?: boolean) => {
    const showVal = _.isNil(shouldShow) ? !show : shouldShow;
    setShow(showVal);
  }, [show]);

  const handleHide = useCallback(() => {
    toggleShow(false);
    setIncludeList(includeList.map((_val: number) => _val = 0));
  }, []);

  const handleSelectAll = () => setIncludeList(includeList.map((val: number) => val = 1));


  // clean up for modal
  useEffect(() => {
    return () => {
      setShow(false)
      setIncludeList(includeList.map((_val: number) => _val = 0));
    }
  }, [])


  return (
    <>
      <Modal onHide={handleHide} show={show}>
        <BootstrapModal.Header closeButton>
          <BootstrapModal.Title>CSV download</BootstrapModal.Title>
        </BootstrapModal.Header>
        <br />
        <>{columns}</>
        <button className='btn btn-dark' style={centerStyle} onClick={handleSelectAll}>
          <span style={downloadCSVButtonStyle}>
            <br />
            <i className="fal fa-check-double fa-x" />&nbsp; Select All
          </span></button>
        <button className='btn btn-dark' style={centerStyle} disabled={!includeList.find((val => val !== 0))} onClick={downloadAction}>
          <span style={downloadCSVButtonStyle}>
            <br />
            <i className="fal fa-file-excel fa-2x" />
            &nbsp; Download
          </span>
        </button>
        <BootstrapModal.Footer>
          <Button variant="primary" onClick={handleHide}>
            OK
          </Button>
        </BootstrapModal.Footer>
      </Modal>
    </>
  );
};

export const CashieringTable: React.FunctionComponent<{
  handleSetSelectedRow: (index: number | undefined) => void;
  selectedRowIndex?: number;
}> = ({ handleSetSelectedRow, selectedRowIndex }) => {
  const { dispatch } = useDispatcher();
  const { data, total, timeframe, ...filterData } = useCashieringData();
  const { sortBy, sortByOrder, pageSize, page } = filterData;
  const [isFetching, hasData] = useStateSelector(isFetchingCashieringSearchEntities);
  const cashieringSearch = sessionStorage.getItem('cashieringSearch');

  useEffect(() => {
    if (hasData && !isFetching) {
      if (!cashieringSearch) return;
      const cashieringSearchData = JSON.parse(cashieringSearch);
      const { index } = cashieringSearchData;
      handleSetSelectedRow(index);
      // scroll into view if the row is not visible
      setTimeout(() => {
        const row = document.querySelector('.selected-row');
        if (row) {
          row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 750);
      // clear the session storage
      sessionStorage.removeItem('cashieringSearch');
    }
  }, [hasData, isFetching, cashieringSearch]);

  const rowClassName = useCallback((row: any) => {
    if (row.index === selectedRowIndex) {
      return 'selected-row';
    }
    return '';
  }, [selectedRowIndex]);

  useEffect(() => {
    dispatch(AdminCashieringActions.cashieringSearchRequest(filterData));
  }, [dispatch]);

  const getCurrentSortBy = useCallback(
    (newDesc?: boolean) => {
      if (sortBy) {
        return { id: sortBy, desc: !_.isUndefined(newDesc) ? newDesc : sortByOrder === 'desc' };
      }
      return undefined;
    },
    [sortBy, sortByOrder]
  );

  const initialState = useMemo(() => {
    const currentSortBy = getCurrentSortBy();
    return {
      sortBy: currentSortBy ? [currentSortBy] : [],
      pageSize: _.isUndefined(pageSize) ? 5 : pageSize,
      pageIndex: page || 0,
      columnOrder: [],
    };
  }, [pageSize, page, getCurrentSortBy]);

  const pageCount = useMemo(() => {
    if (pageSize) {
      return _.ceil(total / pageSize);
    }
    return 0;
  }, [pageSize, total]);

  const onSort = useCallback(
    (sort: any[]) => {
      const sortVal = _.first(sort) || getCurrentSortBy(sortByOrder === 'asc');
      if (sortVal) {
        const { id, desc } = sortVal;
        const sortData: Pick<CashieringSearchRequest, 'sortBy' | 'sortByOrder'> = {
          sortBy: id,
          sortByOrder: desc === true ? 'desc' : 'asc',
        };
        dispatch(AdminCashieringSearchActions.setSortBy(sortData));
      } else {
        dispatch(AdminCashieringSearchActions.setSortBy({ sortBy: undefined, sortByOrder: undefined }));
      }
      handleSetSelectedRow(undefined);
    },
    [getCurrentSortBy]
  );

  const onPage = useCallback((page: number) => {
    dispatch(AdminCashieringSearchActions.setPage({ page }));
    handleSetSelectedRow(undefined);
  }, []);

  const onPageSize = useCallback((pageSize: number) => {
    dispatch(AdminCashieringSearchActions.setPageSize({ pageSize }));
    handleSetSelectedRow(undefined);
  }, []);

  //This is used to simplify data, since our columns only support specific lengths
  // We only will allow the transfer text to be 24 chars.
  const simplifiedData = useMemo(
    () =>
      data.map(cashieringItem => {
        if (cashieringItem.transfer && cashieringItem.transfer.text && cashieringItem.transfer.text.length >= 24) {
          cashieringItem.transfer.text = cashieringItem.transfer.text.slice(0, 21) + '...';
        }
        // attach the handleSetSelectedRow function to each row
        cashieringItem.handleSetSelectedRow = handleSetSelectedRow;
        return cashieringItem;
      }),
    [data]
  );

  // Record all column names 
  const columnNames = CashieringAdminReviewColumns.map((col: ColumnWithSorting<CashieringBaseEntity>) => col.id as string).map((value) => {
    if (value === 'AccountName') return DISPLAY_NAMES.accountname;
    else if (value === 'AccountNumber') return DISPLAY_NAMES.accountnumber
    else if (value === 'RepCode') return DISPLAY_NAMES.repcode
    else if (value === 'accountStatus') return DISPLAY_NAMES.accountstatus
    else return value
  })
  // Filter column choices prior to this for consistent index references.
  const excludedColumnsById = ['note', 'action', 'filter'];
  // Calculate inclusions with checkboxes -> EX:[0, 1] -> exclude table header 1 include table header 2
  const [includeList, setIncludeList] = useState<number[]>(Array(CashieringAdminReviewColumns.length).fill(0));
  const [show, setShow] = useState<boolean>(false);
  const updateIncludeList = (index: number) => setIncludeList((includeList as any).toSpliced(index, 1, (includeList[index] === 0 ? 1 : 0)))
  // Return inputs that will in-place manipulate the final included header list as an encoded boolean array
  const recordedModalColumns = useMemo(() => {
    return columnNames.map((col: string, index: number) =>
      !excludedColumnsById.find((val) => val === col) ?
        <><div style={checkboxListStyle} key={index}>
          <input checked={!!includeList[index]} onChange={() => updateIncludeList(index)} style={checkboxStyle} type='checkbox' />{_.capitalize(col)}</div><hr /></> : <></>)
  }, [includeList])

  useMemo(() => {
    setIncludeList(includeList.map(_val => (_val = 0)));
  }, [isFetching]);

  // Export CSV
  const downloadCSV: () => void = () => {
    // Map bool array to the cashiering column order
    const selectionList = includeList.map((num, index) => num && CashieringAdminReviewColumns[index].id === CashieringAdminReviewColumns[index].id ? CashieringAdminReviewColumns[index].id : '');
    const csvData: ({ [DISPLAY_NAMES: string]: (string | Date)[] }) = {
      //to add a column for csv, add here each prop in csvData is a [header name -> column] combination
      //because the data is serialized and chunked later by prop length
      //sequence is the only thing that we will need to calculate
      [DISPLAY_NAMES.accountnumber]: [],
      [DISPLAY_NAMES.accountname]: [],
      [DISPLAY_NAMES.repcode]: [],
      [DISPLAY_NAMES.transaction]: [],
      [DISPLAY_NAMES.status]: [],
      [DISPLAY_NAMES.method]: [],
      [DISPLAY_NAMES.date]: [],
      [DISPLAY_NAMES.accountstatus]: [],
      [DISPLAY_NAMES.banksignatory]: [],
      [DISPLAY_NAMES.transferamount]: [],
      [DISPLAY_NAMES.transferdirection]: []
    };

    // Build CSV data
    data.forEach((row) => {
      Object.entries(row).map(([key, value]) => {
        const lowerKey = key.toLowerCase();
        if (lowerKey === 'accountnumber' && selectionList.find(val => val && val.toLowerCase() === 'accountnumber')) csvData[DISPLAY_NAMES.accountnumber].push(value);
        if (lowerKey === 'accountname' && selectionList.find(val => val && val.toLowerCase() === 'accountname')) csvData[DISPLAY_NAMES.accountname].push(value);
        if (lowerKey === 'repcode' && selectionList.find(val => val && val.toLowerCase() === 'repcode')) csvData[DISPLAY_NAMES.repcode].push(row.repCode.toString());
        if (lowerKey === 'accountstatus' && selectionList.find((val) => val && val.toLowerCase() === 'accountstatus')) csvData[DISPLAY_NAMES.accountstatus].push(value);
        if (lowerKey === 'date' && selectionList.find(val => val && val.toLowerCase() === 'date')) csvData[DISPLAY_NAMES.date].push(new Date(value).toLocaleDateString());
        if (lowerKey === 'type' && selectionList.find(val => val && val.toLowerCase() === 'transaction')) {
          csvData[DISPLAY_NAMES.transaction].push(row.type);
          csvData[DISPLAY_NAMES.status].push(row.status);
        }
        if (selectionList.find(val => val && val.toLowerCase() === 'method')) {
          if (lowerKey === 'transfer' && row.transfer) {
            csvData[DISPLAY_NAMES.method].push("Method: " + row.transfer.mechanism + " " + row.transfer.text);
            csvData[DISPLAY_NAMES.transferamount].push(formatMoney(row.transfer.amount));
            csvData[DISPLAY_NAMES.transferdirection].push(row.transfer.direction === 'Outgoing' ? 'WITHDRAWAL' : 'DEPOSIT');
            if (row.type === 'AchRelationship') csvData[DISPLAY_NAMES.banksignatory].push(row.transfer.bankAccountOwnerNameSecret);
            else csvData[DISPLAY_NAMES.banksignatory].push('undefined');
          } else if (lowerKey === 'relationship' && row.relationship) {
            csvData[DISPLAY_NAMES.method].push("Method: " + row.relationship.approvalMethod + " " + row.relationship.text);
            csvData[DISPLAY_NAMES.transferamount].push('undefined');
            csvData[DISPLAY_NAMES.transferdirection].push('undefined');
            if (row.type === 'AchRelationship') csvData[DISPLAY_NAMES.banksignatory].push(row.relationship.bankAccountOwnerNameSecret);
            else csvData[DISPLAY_NAMES.banksignatory].push('undefined');
          } else if (lowerKey === 'transferwireinstruction' && row.transferWireInstruction) {
            csvData[DISPLAY_NAMES.method].push("Method: " + row.transferWireInstruction.text);
            csvData[DISPLAY_NAMES.transferamount].push('undefined');
            csvData[DISPLAY_NAMES.transferdirection].push('undefined');
            csvData[DISPLAY_NAMES.banksignatory].push('undefined');
          }
        }
        else return
      });
    });
    exportToCsv(csvData, undefined, 'cashieringTable');
  };

  const [showSearchDate, setShowSearchDate] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [search, setSearch] = useState<boolean>(false);

  const { timeframe: time } = useCashieringData();
  const dateRef = useRef([startDate, endDate])

  //Search total 
  useEffect(() => {
    if (startDate && endDate && search) {
      dispatch(AdminCashieringActions.setTimeframe({ startDate: dateRef.current[0], endDate: dateRef.current[1] }))
      dispatch(AdminCashieringSearchActions.setPageSize({ pageSize: total }));
      setSearch(false);
    }
  }, [endDate, startDate, time, dateRef.current, search])

  const handleDateInput = (event: any) => {
    if (event.target.name === 'start') {
      dateRef.current[0] = event.target.value;
      setStartDate(dateRef.current[0]);
    }
    else if (event.target.name === 'end') {
      dateRef.current[1] = event.target.value;
      setEndDate(event.target.value);
    }
  }

  const handleReset = (event: any) => {
    if (event.target.name === 'reset') {
      dateRef.current = [undefined, undefined];
      dispatch(AdminCashieringActions.setTimeframe({ startDate: dayjs(new Date().toDateString()).subtract(1, 'week') as any, endDate: new Date() }))
      dispatch(AdminCashieringSearchActions.setPageSize({ pageSize: 25 }));
    }
  }

  const resetDateButtonStyle = {
    textDecoration: 'underline',
    marginBottom: '10px',
    paddingLeft: '10px',
    opacity: (!startDate || !endDate) ? 0.3 : 1,
    cursor: (!startDate || !endDate) ? 'not-allowed' : 'inherit'
  } as object

  return (
    <>
      {isFetching && <Loading />}
      <useParentSizeContext.Provider>
        {!isFetching && (
          <>
            {hasData && (
              <div style={csvMainActionButtonContainerStyle}>
                <button onClick={() => setShow(true)}>
                  <span style={csvMainActionButtonStyle}>
                    CSV Download &nbsp;
                    <i className="fal fa-file-excel fa-2x" />
                  </span>
                  <span>{data.length + ' rows selected.'}</span>
                </button>
              </div>
            )}
            {show && hasData && (
              <CashieringDownloadCSVModal
                downloadAction={downloadCSV}
                columns={recordedModalColumns}
                show={show}
                total={total}
                setShow={setShow}
                setIncludeList={setIncludeList}
                includeList={includeList}
              />
            )}
            <input checked={!!showSearchDate} onChange={() => setShowSearchDate(!showSearchDate)} style={{ ...checkboxStyle }} type='checkbox' /><strong>Search by date</strong>

            {showSearchDate &&
              (<span>
                <br />
                <span >
                  <input
                    style={{
                      color: 'ghostwhite',
                      backgroundColor: 'var(--blue-dark-lighter',
                      marginBottom: '10px',
                      marginTop: '10px'
                    }}
                    className="input input-tall ml-2"
                    value={dateRef.current[0] ? dateRef.current[0] as Date : '' as any}
                    onChange={(e) => handleDateInput(e)}
                    id='csv_StartDate'
                    name='start'
                    type='date'
                    required />&nbsp; Start Date</span>
                <span>
                  <br />
                  <input
                    style={{
                      color: 'ghostwhite',
                      backgroundColor: 'var(--blue-dark-lighter'
                    }}
                    className="input input-tall ml-2"
                    value={dateRef.current[1] ? dateRef.current[1] as Date : '' as any}
                    onChange={(e) => handleDateInput(e)}
                    id='csv_EndDate'
                    name='end'
                    type='date'
                    required />&nbsp; End Date</span>
                &nbsp;<br /><Button onClick={() => setSearch(true)} style={{ width: 'max-content', marginTop: '10px', marginLeft: '0.6em' }} color='var(--green)' variant='primary'>Search</Button>
                <hr />
              </span>)}
            <button disabled={!startDate || !endDate} style={resetDateButtonStyle} onClick={(e) => handleReset(e)} id='reset' name='reset'>Reset date search</button>
            {hasData && (
              <CashieringTableContent
                columns={CashieringAdminReviewColumns}
                data={simplifiedData}
                initialState={initialState}
                sortable
                hasPagination
                manualSortBy
                manualPagination
                onSort={onSort}
                onPage={onPage}
                onPageSize={onPageSize}
                pageCount={pageCount}
                columnWidths={['10%', '10%', '5%', '25%', '10%', '10%', '10%', '5%', '5%', '5%']}
                rowClass={(row: any) => rowClassName(row)}
              />
            )}
            {!hasData && <p style={{ padding: '5px' }}>No matching items found</p>}
          </>
        )}
      </useParentSizeContext.Provider>
    </>
  );
};

const csvMainActionButtonContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  paddingTop: '5px',
  paddingBottom: '5px',
  border: 'white',
  borderColor: 'var(--blue)',
  borderStyle: 'solid',
  borderWidth: '1px',
  marginTop: '20px',
  marginBottom: '20px',
  paddingLeft: '10px',
  paddingRight: '10px',
  borderRadius: '10px',
  maxWidth: 'fit-content'
} as object

const csvMainActionButtonStyle = {
  display: 'flex',
  fontWeight: 'bold',
  alignItems: 'center',
} as object;

const centerStyle = {
  borderRadius: '0px',
  display: 'flex',
  paddingBottom: '1rem',
  flexDirection: 'row',
  justifyContent: 'center',
} as object;

const downloadCSVButtonStyle = {
  width: '25%',
  margin: '0 auto',
  borderRadius: '10px',
} as object;

const checkboxListStyle = {
  padding: '0.3rem',
} as object

const checkboxStyle = {
  transform: 'scale(1.5, 1.5)',
  marginLeft: '0.5rem',
  marginRight: '0.8rem',
} as object;

const DISPLAY_NAMES = {
  accountnumber: "Account Number",
  accountname: "Account Name",
  repcode: "Rep code",
  accountstatus: "Account status",
  transaction: "Transaction",
  method: "Method",
  banksignatory: "Bank Signatory",
  transferamount: "Transfer Amount",
  transferdirection: "Transfer Direction",
  date: "Date",
  status: "Status"
} 